<script>
import { money, dateTime, countdown } from "./format";

export default {
  props: {
    price: {
      type: Number,
      default: 0,
    },
    invoice: {
      type: Object,
      default: null,
    },
    secondsRemaining: {
      type: Number,
      default: 0,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    approved: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    expired() {
      return this.invoice !== null && !this.invoice.processing && (this.invoice.status === "expired" || this.secondsRemaining <= 0);
    },
  },
  methods: {
    money,
    dateTime,
    countdown,
    copied() {
      this.$toast.success("Código PIX copiado. Cole no aplicativo do banco para pagar.");
    },
  },
};
</script>

<template>
  <div>
    <!-- Pagamento aprovado (transição para a etapa 2) -->
    <div v-if="approved" class="text-center py-4">
      <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" style="fill: rgba(53, 195, 147, 1);">
        <path d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zm0 18c-4.411 0-8-3.589-8-8s3.589-8 8-8 8 3.589 8 8-3.589 8-8 8z"></path>
        <path d="M9.999 13.587 7.7 11.292l-1.412 1.416 3.713 3.705 6.706-6.706-1.414-1.414z"></path>
      </svg>
      <h5 class="mt-3 mb-1">Pagamento confirmado!</h5>
      <p class="text-muted mb-0">1 crédito liberado. Vamos escolher o seu atendimento.</p>
    </div>

    <!-- Plano sem valor configurado -->
    <div v-else-if="!price || price <= 0" class="text-center py-4">
      <h5 class="mb-2">Atendimento indisponível para o seu plano</h5>
      <p class="text-muted mb-0">Entre em contato com o suporte para mais informações.</p>
    </div>

    <!-- PIX em geração -->
    <div v-else-if="invoice && invoice.processing" class="text-center py-4">
      <b-spinner variant="default" role="status"></b-spinner>
      <p class="text-muted mt-3 mb-0">Gerando o seu pagamento, aguarde...</p>
    </div>

    <!-- PIX expirado -->
    <div v-else-if="expired" class="text-center py-4">
      <h5 class="mb-2">Este PIX expirou sem pagamento</h5>
      <p class="text-muted">Gere um novo pagamento para continuar.</p>
      <button class="btn btn-default btn-block btn-lg" :disabled="loading" @click="$emit('generate-pix')">
        Gerar novo pagamento
        <b-spinner v-if="loading" small class="ml-2 align-middle" variant="dark" role="status"></b-spinner>
      </button>
    </div>

    <!-- Aguardando pagamento -->
    <div v-else-if="invoice" class="text-center">
      <p class="mb-1 text-muted">Valor do atendimento</p>
      <h4 class="mb-3">{{ money(invoice.value) }}</h4>

      <div v-if="invoice.qrcode" class="my-3 d-flex justify-content-center">
        <div class="border rounded p-3">
          <img :src="invoice.qrcode" alt="QR Code PIX" style="width: 200px; max-width: 100%;" />
        </div>
      </div>

      <p class="text-left mb-2">Abra o app do seu banco, escolha PIX copia e cola e cole o código abaixo, ou leia o QR Code.</p>
      <div class="border rounded p-3 mb-3 text-left text-break font-size-12">{{ invoice.copy_paste }}</div>
      <button type="button" class="btn btn-lg btn-default btn-block" v-clipboard:copy="invoice.copy_paste" @click="copied">
        Copiar código PIX
      </button>

      <div class="d-flex align-items-center justify-content-center mt-4">
        <b-spinner small variant="default" role="status" class="mr-2"></b-spinner>
        <span>Aguardando confirmação do pagamento...</span>
      </div>
      <p class="text-muted font-size-13 mt-2 mb-1">PIX válido até {{ dateTime(invoice.expiration) }} ({{ countdown(secondsRemaining) }})</p>
      <p class="text-muted font-size-13 mb-0">Pode sair desta página: ao voltar, o pagamento continuará aqui.</p>
    </div>

    <!-- Sem fatura: gerar pagamento -->
    <div v-else>
      <div class="text-center mb-4">
        <p class="mb-1 text-muted">Valor referente a Triagem + Consulta marcada</p>
        <h3 class="mb-0">{{ money(price) }}</h3>
      </div>
      <ul class="list-unstyled mb-4">
        <li class="d-flex align-items-center mb-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" class="mr-2" style="fill: rgba(53, 195, 147, 1);"><path d="m10 15.586-3.293-3.293-1.414 1.414L10 18.414l9.707-9.707-1.414-1.414z"></path></svg>
          Pagamento via PIX
        </li>
        <li class="d-flex align-items-center mb-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" class="mr-2" style="fill: rgba(53, 195, 147, 1);"><path d="m10 15.586-3.293-3.293-1.414 1.414L10 18.414l9.707-9.707-1.414-1.414z"></path></svg>
          Liberação automática após o pagamento
        </li>
        <li class="d-flex align-items-center">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" class="mr-2" style="fill: rgba(53, 195, 147, 1);"><path d="m10 15.586-3.293-3.293-1.414 1.414L10 18.414l9.707-9.707-1.414-1.414z"></path></svg>
          Link do atendimento válido por 1 hora após iniciar
        </li>
      </ul>
      <button class="btn btn-default btn-block btn-lg" :disabled="loading" @click="$emit('generate-pix')">
        Gerar PIX
        <b-spinner v-if="loading" small class="ml-2 align-middle" variant="dark" role="status"></b-spinner>
      </button>
    </div>
  </div>
</template>
